/**
 * RaceDetailDialog — popup with full race details and checkpoint list.
 */

import { Box, Dialog, DialogContent, DialogTitle, Divider, IconButton, Typography, useTheme, Chip, Link } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RouteIcon from '@mui/icons-material/Route';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TimerIcon from '@mui/icons-material/Timer';
import FlagIcon from '@mui/icons-material/Flag';
import { tokens } from '../../theme';
import RunningCheckpointsGrid from './RunningCheckpointsGrid';

const StatChip = ({ icon, label, value, color }) => (
  <Box display="flex" alignItems="center" gap={0.75} sx={{ minWidth: 100 }}>
    {icon}
    <Box>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.1 }}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight="bold" sx={{ color: color || 'text.primary' }}>
        {value}
      </Typography>
    </Box>
  </Box>
);

const fmtMaxTime = (race) => {
  if (race.max_allowed_race_time_hours != null) {
    const h = Math.floor(race.max_allowed_race_time_hours);
    const m = Math.round((race.max_allowed_race_time_hours - h) * 60);
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  }
  if (race.max_race_time) return race.max_race_time;
  return null;
};

export default function RaceDetailDialog({ race, open, onClose }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  if (!race) return null;

  const maxTime = fmtMaxTime(race);
  const title = race.event_name && race.event_name !== race.name
    ? `${race.event_name} — ${race.name}`
    : race.name;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ pb: 1, pr: 6 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <RouteIcon sx={{ color: colors.blueAccent[400] }} />
          <Box>
            <Typography variant="h6" color={colors.grey[100]} fontWeight="bold">
              {title}
            </Typography>
            {race.country && (
              <Typography variant="caption" color={colors.grey[400]}>
                {race.country}{race.region ? ` · ${race.region}` : ''}
              </Typography>
            )}
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ position: 'absolute', right: 12, top: 12, color: colors.grey[400] }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 0 }}>
        {/* Stats row */}
        <Box
          display="flex"
          flexWrap="wrap"
          gap={3}
          sx={{
            backgroundColor: 'action.hover',
            borderRadius: 1,
            p: 2,
            mb: 2,
          }}
        >
          {race.distance_km != null && (
            <StatChip
              icon={<RouteIcon sx={{ fontSize: 20, color: colors.blueAccent[400] }} />}
              label="Distance"
              value={`${race.distance_km.toFixed(1)} km`}
              color={colors.grey[100]}
            />
          )}
          {race.elevation_gain_m != null && (
            <StatChip
              icon={<TrendingUpIcon sx={{ fontSize: 20, color: colors.greenAccent[400] }} />}
              label="Elevation gain"
              value={`+${race.elevation_gain_m.toFixed(0)} m`}
              color={colors.greenAccent[300]}
            />
          )}
          {race.start_location && (
            <StatChip
              icon={<LocationOnIcon sx={{ fontSize: 20, color: colors.grey[400] }} />}
              label="Start"
              value={race.start_location}
              color={colors.grey[100]}
            />
          )}
          {race.race_start && (
            <StatChip
              icon={<FlagIcon sx={{ fontSize: 20, color: colors.grey[400] }} />}
              label="Race start"
              value={race.race_start}
              color={colors.grey[100]}
            />
          )}
          {maxTime && (
            <StatChip
              icon={<TimerIcon sx={{ fontSize: 20, color: colors.redAccent[400] }} />}
              label="Time limit"
              value={maxTime}
              color={colors.redAccent[300]}
            />
          )}
        </Box>

        {/* Description */}
        {race.description && (
          <Typography variant="body2" color={colors.grey[300]} sx={{ mb: 2 }}>
            {race.description}
          </Typography>
        )}

        {/* Website link */}
        {race.website_url && (
          <Box mb={2}>
            <Link
              href={race.website_url}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              sx={{ color: colors.blueAccent[400], display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
            >
              <OpenInNewIcon sx={{ fontSize: 14 }} />
              <Typography variant="body2">Official race page</Typography>
            </Link>
          </Box>
        )}

        {/* Checkpoints */}
        {race.checkpoints?.length > 0 && (
          <>
            <Divider sx={{ borderColor: colors.grey[700], mb: 2 }} />
            <RunningCheckpointsGrid
              race={race}
              checkpoints={[]}
              title="Checkpoints"
            />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
